import { useMutation } from '@tanstack/react-query'

import { Logger } from '@core/logger'
import { queryClient } from '@core/query'
import { FollowQueryKey } from '../helpers/queryKeys'
import { resetFollowers, resetFollowing } from '../helpers/followCache'

import {
    applyFollowState,
    restoreFollowState,
    snapshotFollowState,
    type TFollowSnapshot,
} from '../helpers/followOptimistic'
import { startFollow } from '../services/startFollow'

export type TFollowVariables = {
    username: string
    myUsername: string
}

/**
 * Follow a user. Optimistically flips the follow flag and bumps both counts (target's followers,
 * viewer's following), rolling back on error. On success the affected follower/following lists are
 * invalidated so they refetch.
 */
export const useFollow = () => {
    return useMutation<unknown, Error, TFollowVariables, TFollowSnapshot>({
        mutationFn: ({ username }) => startFollow(username),
        onMutate: async ({ username, myUsername }) => {
            // Cancel every query the optimistic update overwrites (flag + both stat counts) — an
            // in-flight fetch resolving after our setData would otherwise clobber the optimistic value.
            await Promise.all([
                queryClient.cancelQueries({ queryKey: FollowQueryKey.followCheck(username) }),
                queryClient.cancelQueries({ queryKey: FollowQueryKey.followStatistics(username) }),
                queryClient.cancelQueries({
                    queryKey: FollowQueryKey.followStatistics(myUsername),
                }),
            ])

            const snapshot = snapshotFollowState(username, myUsername)
            applyFollowState(username, myUsername, true)

            return snapshot
        },
        onError: (error, { username, myUsername }, snapshot) => {
            Logger.error('Follow error', { error })

            if (snapshot) {
                restoreFollowState(username, myUsername, snapshot)
            }
        },
        onSuccess: (_data, { username, myUsername }) => {
            resetFollowers(username)
            resetFollowing(myUsername)
        },
    })
}
